// =====================================================
// UNLINK PREVIEW DIALOG - Modal de confirmación con cascada
// =====================================================

import { useEffect, useRef } from 'react';
import { AlertTriangle, X, Loader2 } from 'lucide-react';
import type { UnlinkPreviewResponse } from '../../types/catalog';

interface UnlinkPreviewDialogProps {
  isOpen: boolean;
  isLoading: boolean;
  isUnlinking: boolean;
  preview: UnlinkPreviewResponse | null;
  onClose: () => void;
  onConfirm: () => void;
}

// Traducción de tipos
const typeTranslations: Record<string, string> = {
  specialty: 'Especialidad',
  subspecialty: 'Subespecialidad',
  treatmentGroup: 'Grupo de tratamiento',
  medicalAct: 'Acto médico',
  Subespecialidad: 'Subespecialidad',
  Grupo: 'Grupo de tratamiento',
  Acto: 'Acto médico',
};

export function UnlinkPreviewDialog({
  isOpen,
  isLoading,
  isUnlinking,
  preview,
  onClose,
  onConfirm,
}: UnlinkPreviewDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);

  // Cerrar con Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !isUnlinking) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, isUnlinking, onClose]);

  // Bloquear scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const hasChildren = preview && preview.childrenToUnlink.length > 0;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={!isUnlinking ? onClose : undefined}
      />

      {/* Dialog */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          ref={dialogRef}
          className="relative bg-white rounded-xl shadow-xl max-w-lg w-full transform transition-all"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button */}
          <button
            onClick={onClose}
            disabled={isUnlinking}
            className="absolute top-4 right-4 p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>

          <div className="p-6">
            {/* Loading state */}
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-8">
                <Loader2 className="h-8 w-8 text-primary animate-spin mb-4" />
                <p className="text-sm text-gray-600">Calculando elementos afectados...</p>
              </div>
            ) : preview ? (
              <>
                {/* Icon */}
                <div className="mx-auto w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mb-4">
                  <AlertTriangle className="h-6 w-6 text-amber-600" />
                </div>

                {/* Content */}
                <div className="text-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    ¿Desvincular {preview.itemName}?
                  </h3>
                  <p className="text-sm text-gray-600">
                    {hasChildren
                      ? `Esta acción desvinculará ${preview.totalItems} elemento${preview.totalItems > 1 ? 's' : ''} en cascada.`
                      : 'Este elemento se desvinculará del centro.'}
                  </p>
                </div>

                {/* Children list */}
                {hasChildren && (
                  <div className="mb-6">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                      Elementos que se desvincularán:
                    </p>
                    <div className="max-h-48 overflow-y-auto bg-gray-50 rounded-lg p-3 space-y-1">
                      {preview.childrenToUnlink.map((child, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-2 text-sm text-gray-700"
                        >
                          <span className="w-2 h-2 rounded-full bg-amber-400 flex-shrink-0" />
                          <span className="text-gray-500">
                            {typeTranslations[child.type] || child.type}:
                          </span>
                          <span className="font-medium truncate">{child.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-3">
                  <button
                    onClick={onClose}
                    disabled={isUnlinking}
                    className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={onConfirm}
                    disabled={isUnlinking}
                    className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {isUnlinking ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Desvinculando...
                      </>
                    ) : (
                      'Desvincular'
                    )}
                  </button>
                </div>
              </>
            ) : (
              <div className="text-center py-8">
                <p className="text-sm text-red-600">Error al cargar la información</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default UnlinkPreviewDialog;
